import React from 'react';
import { Icon } from '@/shared/icons';

/**
 * Props do FilterChip
 */
export interface FilterChipProps {
  /** Nome do filtro (ex: "Prioridade") */
  label: string;
  /** Valor formatado (ex: "Alta, Média") */
  value: string;
  /** Callback ao clicar no X para remover */
  onRemove: () => void;
  /** Callback ao clicar no chip para editar */
  onClick: () => void;
  /** testid do chip; o X de remover recebe o mesmo com sufixo `-remove`. */
  'data-testid'?: string;
}

/**
 * Chip compacto que exibe um filtro ativo.
 * Clicável para editar, com botão X para remover.
 *
 * @example
 * ```tsx
 * <FilterChip
 *   label="Prioridade"
 *   value="Alta, Média"
 *   onClick={() => handleEdit('priority')}
 *   onRemove={() => handleRemove('priority')}
 * />
 * ```
 */
export const FilterChip: React.FC<FilterChipProps> = ({
  label,
  value,
  onRemove,
  onClick,
  'data-testid': dataTestId,
}) => {
  return (
    /* `.fchip` do mockup (DS 3.3): 26px, pílula lilás #f3e8ff, texto #7c3aed 12px
       semibold, botão × redondo 18px. Vale para TODAS as filterbars. */
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 h-[26px] pl-[11px] pr-[5px] rounded-full text-xs font-semibold whitespace-nowrap flex-none transition-colors cursor-pointer"
      style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}
      data-testid={dataTestId}
    >
      {/* Mockup `.fchip`: frase única uniforme "Temperatura quente" (label + valor, sem "Label:") */}
      <span>{label} {value}</span>
      <span
        role="button"
        tabIndex={-1}
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="inline-flex items-center justify-center w-[18px] h-[18px] rounded-full transition-all"
        style={{ color: 'inherit', opacity: 0.6 }}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = '1';
          e.currentTarget.style.backgroundColor = 'rgba(0,0,0,.08)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = '0.6';
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
        aria-label="remover filtro"
        data-testid={dataTestId ? `${dataTestId}-remove` : undefined}
      >
        <Icon name="x" className="w-3 h-3" />
      </span>
    </button>
  );
};

FilterChip.displayName = 'FilterChip';
